import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";

interface MultiSelectDropdownProps {
  options: Array<{
    value: string | number;
    label: string;
    disabled?: boolean;
    description?: string;
  }>;
  value?: (string | number)[];
  onChange?: (values: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxDisplayItems?: number;
  searchable?: boolean;
  clearable?: boolean;
  closeOnSelect?: boolean;
  maxSelection?: number;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  disabled = false,
  className = "",
  maxDisplayItems = 3,
  searchable = true,
  clearable = true,
  closeOnSelect = false,
  maxSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const normalizeValues = (val: any): (string | number)[] => {
    if (Array.isArray(val)) return val;
    if (val !== undefined && val !== null && val !== "") return [val];
    return [];
  };

  const [selectedValues, setSelectedValues] = useState<(string | number)[]>(
    normalizeValues(value),
  );
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValues((prev) => {
        const safe = normalizeValues(value);
        if (JSON.stringify(prev) !== JSON.stringify(safe)) {
          return safe;
        }
        return prev;
      });
    }
  }, [value]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm, searchable]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setSearchTerm("");
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const toggleOption = (optValue: string | number) => {
    let newValues = [...selectedValues];
    const index = newValues.indexOf(optValue);

    if (index > -1) {
      newValues.splice(index, 1);
    } else {
      if (maxSelection && newValues.length >= maxSelection) return;
      newValues.push(optValue);
    }

    setSelectedValues(newValues);
    onChange?.(newValues);

    if (closeOnSelect) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValues([]);
    onChange?.([]);
  };



  return (
    <div
      ref={dropdownRef}
      className={`relative w-full border rounded-lg px-2 py-1.5 bg-white cursor-pointer min-h-[44px] flex items-center transition-all duration-200 ${
        isOpen ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-300 hover:border-blue-400"
      } ${
        disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200" : "text-gray-900"
      } ${className}`}
      onClick={handleToggle}
    >
      <div className="flex flex-wrap items-center gap-1 flex-1 pr-6">
        {selectedValues.slice(0, maxDisplayItems).map((val) => {
          const opt = options.find((o) => o.value === val);
          if (!opt) return null;
          return (
            <span
              key={val}
              className="flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              {opt.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(val);
                  }}
                  className="hover:text-red-500 hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                >
                  <X size={12} />
                </button>
              )}
            </span>
          );
        })}

        {selectedValues.length > maxDisplayItems && (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span 
                  className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium cursor-help" 
                  onClick={e => e.stopPropagation()}
                >
                  +{selectedValues.length - maxDisplayItems} more
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-1">
                  {selectedValues
                    .slice(maxDisplayItems)
                    .map((val) => {
                       const opt = options.find(o => o.value === val);
                       return opt ? <span key={val} className="text-sm">{opt.label}</span> : null;
                    })}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {searchable && options.length > 5 ? (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => {
              // Removed auto-open on focus to prevent opening on modal mount or tab navigation
            }}
            onClick={(e) => {
              if (isOpen) e.stopPropagation();
              else if (!disabled) setIsOpen(true);
            }}
            placeholder={selectedValues.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[60px] bg-transparent outline-none truncate text-sm text-gray-900"
            disabled={disabled}
          />
        ) : (
          selectedValues.length === 0 && (
            <span className="truncate text-sm text-gray-400 px-1">{placeholder}</span>
          )
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0 absolute right-2 top-1/2 -translate-y-1/2">
        {clearable && selectedValues.length > 0 && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700 transition-colors"
            title="Clear all"
          >
            <X size={14} />
          </button>
        )}
        <div className={`transition-transform duration-200 text-gray-500 ${isOpen ? "rotate-180" : ""}`}>
          <ChevronDown size={16} />
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-[100] left-0 right-0 mt-1 border border-gray-200 rounded-lg bg-white shadow-lg max-h-60 flex flex-col"
        >
          <div className="overflow-y-auto">
            {filteredOptions.length > 0 && (!maxSelection || maxSelection > 1) && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const safeValues = Array.isArray(selectedValues) ? selectedValues : [];
                  const selectableOptions = filteredOptions.filter(o => !o.disabled);
                  if (selectableOptions.length === 0) return;
                  const allSelected = selectableOptions.every(o => safeValues.includes(o.value));
                  let newValues: (string | number)[];
                  if (allSelected) {
                    newValues = safeValues.filter(v => !selectableOptions.find(o => o.value === v));
                  } else {
                    newValues = Array.from(new Set([...safeValues, ...selectableOptions.map(o => o.value)]));
                    if (maxSelection && newValues.length > maxSelection) {
                      newValues = newValues.slice(0, maxSelection);
                    }
                  }
                  setSelectedValues(newValues);
                  onChange?.(newValues);
                }}
                className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 font-medium text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={filteredOptions.filter(o => !o.disabled).length > 0 && filteredOptions.filter(o => !o.disabled).every(o => (Array.isArray(selectedValues) ? selectedValues : []).includes(o.value))}
                  readOnly
                  className="mr-2"
                />
                {filteredOptions.filter(o => !o.disabled).length > 0 && filteredOptions.filter(o => !o.disabled).every(o => (Array.isArray(selectedValues) ? selectedValues : []).includes(o.value)) ? "Deselect All" : "Select All"}
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div
                onClick={(e) => e.stopPropagation()}
                className="px-3 py-2 text-sm text-gray-500"
              >
                {searchTerm ? "No options found" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <div
                    key={String(option.value)}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (!option.disabled) {
                        toggleOption(option.value);
                      }
                    }}
                    className={`flex items-center px-3 py-2 cursor-pointer ${
                      option.disabled
                        ? "text-gray-400 cursor-not-allowed"
                        : isSelected
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    title={option.description}
                  >
                    <div className={`w-4 h-4 mr-3 border rounded flex items-center justify-center shrink-0 transition-colors ${
                      isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                    } ${option.disabled ? "border-gray-200 bg-gray-100" : ""}`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{option.label}</div>
                      {option.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
